import { Building2, Check, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFeatureFlagEditor } from '../hooks/use-feature-flag-editor'
import {
  FEATURE_FLAG_AUDIENCES,
  type FeatureFlagAudience,
  type FeatureFlagDTO,
} from '@/schemas/feature-flag.schema'
import {
  AUDIENCE_LABELS,
  audienceLabel,
} from '../lib/feature-flag-audience-labels'

// Shown in place of a count. It is a gap to fill, not a group that gets the feature.
const NOBODY_LISTED = 'No whitelisted company yet'

export function FeatureFlagEditor({ flag }: { flag: FeatureFlagDTO }) {
  const editor = useFeatureFlagEditor(flag)
  const { audience, whitelist } = editor.rule
  // -1 for a tier this build does not know.
  const chosen = FEATURE_FLAG_AUDIENCES.indexOf(audience)
  const reaches = (tier: FeatureFlagAudience) =>
    chosen >= FEATURE_FLAG_AUDIENCES.indexOf(tier)
  // Who gets the feature, by environment. Dev / Local is an environment and
  // not a group of people, so it is a row of its own and not an entry in the
  // list. The same two rows at every tier, so the screen keeps its shape.
  const onProduction = reaches('EVERYONE')
    ? ['Everyone']
    : (['SUPPORT', 'INTERNAL_COMPANIES', 'POWERDECKER', 'WHITELIST'] as const)
        .filter(reaches)
        .map((tier) =>
          tier === 'WHITELIST'
            ? whitelist.length === 0
              ? NOBODY_LISTED
              : `${whitelist.length} whitelisted ${whitelist.length === 1 ? 'company' : 'companies'}`
            : AUDIENCE_LABELS[tier].group,
        )
  return (
    <section className="ff-detail">
      <header className="ff-detail-header">
        <div className="ff-title-row">
          <div>
            <p className="ff-eyebrow">FEATURE FLAG</p>
            <h1>{flag.name}</h1>
          </div>
        </div>
        <code className="ff-key">{flag.key}</code>
        <p className="ff-intro">{flag.description}</p>
      </header>
      <div className="ff-editor-layout">
        <div className="ff-editor-content">
          <fieldset disabled={editor.mutation.isPending} className="ff-rules">
            <legend className="sr-only">Audience for {flag.name}</legend>
            <div className="ff-audience-section">
              <div className="ff-section-heading">
                <h2>Who gets this feature?</h2>
              </div>
              <Label htmlFor={`${flag.key}-audience`}>Audience</Label>
              <Select
                value={audience}
                onValueChange={(value) =>
                  editor.update('audience', value as FeatureFlagAudience)
                }
              >
                <SelectTrigger
                  id={`${flag.key}-audience`}
                  aria-label="Audience"
                  className="ff-audience-trigger"
                >
                  <SelectValue>{audienceLabel(audience)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {/* A tier this build does not know stays selectable, so
                      opening the dropdown does not move off it. */}
                  {chosen === -1 ? (
                    <SelectItem value={audience}>
                      {audienceLabel(audience)}
                    </SelectItem>
                  ) : null}
                  {FEATURE_FLAG_AUDIENCES.map((tier) => (
                    <SelectItem key={tier} value={tier}>
                      {AUDIENCE_LABELS[tier].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {chosen === -1 ? (
                <p className="ff-audience-unknown">
                  This console does not know the tier {audience} yet. Update the
                  web app before changing this flag.
                </p>
              ) : (
                <dl className="ff-audience-summary">
                  <div>
                    <dt>Production</dt>
                    <dd data-testid="on-production">
                      {onProduction.length === 0 ? (
                        <span className="ff-nobody">Nobody</span>
                      ) : (
                        onProduction.map((group) => (
                          <span
                            key={group}
                            className={
                              group === NOBODY_LISTED ? 'ff-nobody' : undefined
                            }
                          >
                            {group}
                          </span>
                        ))
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Local and development</dt>
                    <dd data-testid="on-local">
                      {reaches('DEV_OR_LOCAL') ? (
                        <span>Everyone</span>
                      ) : (
                        <span className="ff-nobody">Nobody</span>
                      )}
                    </dd>
                  </div>
                </dl>
              )}
              {audience === 'WHITELIST' ? (
                <div className="ff-whitelist">
                  <div className="ff-company-heading">
                    <div>
                      <h2>
                        Whitelist <span>{whitelist.length}</span>
                      </h2>
                      <p>
                        Search by company name or ID, or paste a list of IDs,
                        and add companies to this flag.
                      </p>
                    </div>
                  </div>
                  <div className="ff-company-picker">
                    <Label htmlFor={`${flag.key}-company-search`}>
                      Search companies by name or ID
                    </Label>
                    <div className="ff-company-search-input">
                      <Search size={16} aria-hidden="true" />
                      <Input
                        id={`${flag.key}-company-search`}
                        placeholder="Company name, ID, or a list of IDs…"
                        autoComplete="off"
                        value={editor.search}
                        onChange={(event) =>
                          editor.setSearch(event.target.value)
                        }
                      />
                    </div>
                    {editor.search.trim() ? (
                      <div
                        className="ff-company-results"
                        aria-label="Company search results"
                      >
                        {editor.searching ? (
                          <p role="status">Searching companies…</p>
                        ) : editor.companies.isError ? (
                          <div role="alert">
                            Could not search companies.
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editor.companies.refetch()}
                            >
                              Try again
                            </Button>
                          </div>
                        ) : (
                          <>
                            {editor.pastedIds.length ? (
                              <PastedIds editor={editor} />
                            ) : null}
                            {editor.companies.data?.items.map((company) => {
                              const added = editor.rule.whitelist.includes(
                                company.id,
                              )
                              return (
                                <button
                                  type="button"
                                  className="ff-company-result"
                                  key={company.id}
                                  disabled={added}
                                  aria-label={
                                    added
                                      ? `${company.name} already added`
                                      : `Add ${company.name}`
                                  }
                                  onClick={() =>
                                    editor.addCompany(company.id, company.name)
                                  }
                                >
                                  <span>
                                    {company.name}
                                    <code>#{company.id}</code>
                                  </span>
                                  <span>
                                    {added ? (
                                      <Check size={14} />
                                    ) : (
                                      <Plus size={14} />
                                    )}
                                    {added ? 'Added' : 'Add'}
                                  </span>
                                </button>
                              )
                            })}
                            {editor.companies.data?.items.length === 0 ? (
                              <p role="status">
                                No companies found. Try another name or ID.
                              </p>
                            ) : null}
                            {editor.companies.data?.pagination?.hasMore ? (
                              <p>
                                Showing the first 50 matches. Refine your search
                                to find a specific company.
                              </p>
                            ) : null}
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div className="ff-selected-heading">Selected companies</div>
                  {editor.rule.whitelist.length === 0 ? (
                    <p className="ff-no-companies">No companies added.</p>
                  ) : null}
                  <div className="ff-company-list">
                    {editor.rule.whitelist.map((id) => (
                      <div key={id} className="ff-company">
                        <span className="ff-company-avatar">
                          <Building2 size={15} />
                        </span>
                        <span className="ff-company-name">
                          {editor.names[id] ?? 'Name unavailable'}
                        </span>
                        <code>#{id}</code>
                        <button
                          type="button"
                          aria-label={`Remove company ${id}`}
                          onClick={() => editor.removeCompany(id)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </fieldset>
          {editor.dirty || editor.mutation.isPending ? (
            <footer className="ff-savebar ff-detail-actions">
              <span className="ff-unsaved">
                <span />
                Unsaved changes
              </span>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={editor.mutation.isPending}
                  onClick={editor.reset}
                >
                  Discard
                </Button>
                <Button
                  className="ff-save-button"
                  size="sm"
                  disabled={editor.mutation.isPending}
                  onClick={editor.save}
                >
                  {editor.mutation.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </footer>
          ) : null}
        </div>
      </div>
    </section>
  )
}

// A pasted list of ids: one button for all of them, and the ids that match no company.
function PastedIds({
  editor,
}: {
  editor: ReturnType<typeof useFeatureFlagEditor>
}) {
  const found = editor.companies.data?.items ?? []
  const toAdd = found.filter(
    (company) => !editor.rule.whitelist.includes(company.id),
  )
  const missing = editor.pastedIds.filter(
    (id) => !found.some((company) => company.id === id),
  )
  return (
    <div className="ff-company-bulk">
      <Button
        size="sm"
        disabled={toAdd.length === 0}
        onClick={() => editor.addCompanies(toAdd)}
      >
        {toAdd.length
          ? `Add ${toAdd.length} ${toAdd.length === 1 ? 'company' : 'companies'}`
          : 'All added'}
      </Button>
      {missing.length ? (
        <span role="status">
          Not found: {missing.map((id) => `#${id}`).join(', ')}
        </span>
      ) : null}
    </div>
  )
}
